import {
  Component,
  forwardRef,
  HostListener,
  Input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * A number input that cannot silently corrupt itself.
 *
 * - `type="text"` + `inputmode="numeric"` — no native scroll-to-change.
 * - Mouse wheel and arrow keys explicitly blocked, defensively.
 * - Strict digit-only filter on every input event (including paste).
 * - Emits `number | null` (integer shekels) via ControlValueAccessor.
 *
 * Replaces every `<input type="number">` used for money in the app.
 * This is the fix for the 20→19, 50→49 silent corruption bug.
 */
@Component({
  selector: 'app-money-input',
  templateUrl: './money-input.component.html',
  styleUrls: ['./money-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MoneyInputComponent),
      multi: true,
    },
  ],
})
export class MoneyInputComponent implements ControlValueAccessor {
  /** Visible field label (shown above the input, also used as aria-label). */
  @Input() label = '';

  /** Optional helper text shown below the field. */
  @Input() hint: string | null = null;

  /** Visual error message shown below the field when set. */
  @Input() error: string | null = null;

  /** Allow negative values (use for fields like balance display, not price). */
  @Input() allowNegative = false;

  /**
   * Decimal places: 0 = whole shekels (prayer orders — behaviour unchanged),
   * 2 = agorot (synagogue transactions). In decimal mode a refused key or
   * paste is NEVER silently dropped — the field keeps its value and says
   * why. (Silently dropping "." turned 227065.90 into 22706590.)
   */
  @Input() decimals: 0 | 2 = 0;

  /** Decimal mode: why the last keystroke / paste was refused. */
  rejectNotice: string | null = null;

  /** Decimal mode: the parsed value, for the formatted preview. */
  currentValue: number | null = null;

  /** Optional unique id for the inner <input> (for label `for` linkage in parent). */
  @Input() inputId: string | null = null;

  /** Disabled state — managed by Angular forms via setDisabledState. */
  disabled = false;

  /** The string we render in the input (what the user sees). */
  displayValue = '';

  private onChange: (value: number | null) => void = () => {
    // noop until Angular wires it up
  };

  private onTouched: () => void = () => {
    // noop until Angular wires it up
  };

  // ── ControlValueAccessor ────────────────────────────────────────────────

  writeValue(value: unknown): void {
    this.rejectNotice = null;
    if (value === null || value === undefined || value === '') {
      this.displayValue = '';
      this.currentValue = null;
      return;
    }
    if (this.decimals > 0) {
      const n = typeof value === 'number' ? value : Number(value);
      this.displayValue = Number.isFinite(n) ? String(n) : '';
      this.currentValue = Number.isFinite(n) ? n : null;
      return;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      // Trust the value here only as a display hint. If it's fractional we
      // round for display but the *committed* value is whatever the user
      // types in the field. (The schemas reject fractional money on submit.)
      this.displayValue = String(Math.trunc(value));
      return;
    }
    if (typeof value === 'string') {
      const cleaned = this.sanitize(value);
      this.displayValue = cleaned;
      return;
    }
    this.displayValue = '';
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ── Event handlers ──────────────────────────────────────────────────────

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.decimals > 0) {
      // Mobile keyboards / autofill can bypass keydown. Validate the whole
      // value; if it isn't a valid amount, restore the previous one.
      const next = this.sanitizeDecimal(target.value);
      if (next === null) {
        target.value = this.displayValue;
        this.rejectNotice = 'ניתן להזין ספרות ונקודה אחת בלבד (עד 2 ספרות אחרי הנקודה)';
        return;
      }
      this.rejectNotice = null;
      target.value = next;
      this.displayValue = next;
      this.emitDecimal(next);
      return;
    }
    const cleaned = this.sanitize(target.value);
    if (cleaned !== target.value) {
      // Reflect the cleaned value back into the DOM so the cursor doesn't
      // drift past invalid characters.
      target.value = cleaned;
    }
    this.displayValue = cleaned;
    this.emit(cleaned);
  }

  onBlur(): void {
    this.onTouched();
  }

  /**
   * Block the wheel event on the input. Native number inputs change value on
   * scroll — we're `type="text"` so this is belt-and-braces, but if a future
   * change flips the type, this guard still holds.
   */
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    const active = document.activeElement;
    if (active && active.tagName === 'INPUT') {
      (active as HTMLInputElement).blur();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    // Block ↑/↓ which native number inputs use to inc/dec by step.
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      return;
    }

    // Allow control keys (backspace, delete, tab, arrows L/R, home, end,
    // ctrl+a/c/v/x, shift selection, etc).
    if (
      event.key.length > 1 || // named keys
      event.ctrlKey ||
      event.metaKey ||
      event.altKey
    ) {
      return;
    }

    if (this.decimals > 0) {
      this.onDecimalKeydown(event);
      return;
    }

    // Allow minus only at position 0 and only if allowNegative.
    if (event.key === '-') {
      const target = event.target as HTMLInputElement;
      if (
        !this.allowNegative ||
        (target.selectionStart ?? 0) !== 0 ||
        this.displayValue.startsWith('-')
      ) {
        event.preventDefault();
      }
      return;
    }

    // Otherwise allow only digits.
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (this.decimals > 0) {
      this.onDecimalPaste(event);
      return;
    }
    const pasted = event.clipboardData?.getData('text') ?? '';
    const cleaned = this.sanitize(pasted);
    if (cleaned === pasted) {
      // The paste content is already clean; let the browser handle it,
      // then sanitize again on the resulting input event.
      return;
    }
    event.preventDefault();

    const target = event.target as HTMLInputElement;
    const start = target.selectionStart ?? this.displayValue.length;
    const end = target.selectionEnd ?? this.displayValue.length;
    const before = this.displayValue.slice(0, start);
    const after = this.displayValue.slice(end);
    const merged = this.sanitize(before + cleaned + after);

    target.value = merged;
    this.displayValue = merged;
    this.emit(merged);
  }

  // ── Decimal mode (agorot) ───────────────────────────────────────────────

  private onDecimalKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const start = target.selectionStart ?? this.displayValue.length;
    const end = target.selectionEnd ?? this.displayValue.length;
    const dot = this.displayValue.indexOf('.');

    // Period key: "." on an English layout, "ץ" on a Hebrew one, or the
    // numpad decimal key. All of them mean "decimal point".
    if (event.key === '.' || event.code === 'Period' || event.code === 'NumpadDecimal') {
      // Typing a dot over a selection that contains the old dot is fine.
      if (dot !== -1 && !(dot >= start && dot < end)) {
        event.preventDefault();
        this.rejectNotice = 'כבר יש נקודה עשרונית בסכום';
        return;
      }
      if (event.key !== '.') {
        event.preventDefault();
        this.insertAtCaret(target, '.', start, end);
      }
      return;
    }

    if (event.key === ',') {
      event.preventDefault();
      this.rejectNotice = 'לאגורות השתמשו בנקודה (.) ולא בפסיק';
      return;
    }

    if (/^[0-9]$/.test(event.key)) {
      // Block a 3rd digit after the dot (unless it replaces a selection).
      if (dot !== -1 && start > dot && start === end) {
        const fraction = this.displayValue.slice(dot + 1);
        if (fraction.length >= this.decimals) {
          event.preventDefault();
          this.rejectNotice = `עד ${this.decimals} ספרות אחרי הנקודה (אגורות)`;
        }
      }
      return;
    }

    event.preventDefault();
    this.rejectNotice = 'ניתן להזין ספרות ונקודה בלבד';
  }

  private onDecimalPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const raw = (event.clipboardData?.getData('text') ?? '').replace(/[\s₪]/g, '');

    // Accept "1234.56" or properly grouped "1,234.56". Anything else
    // (e.g. "1234,56") is refused — never guessed at.
    let pasted: string | null = null;
    if (/^\d+(\.\d{1,2})?$/.test(raw)) pasted = raw;
    else if (/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(raw)) pasted = raw.replace(/,/g, '');

    const target = event.target as HTMLInputElement;
    const start = target.selectionStart ?? this.displayValue.length;
    const end = target.selectionEnd ?? this.displayValue.length;
    const merged =
      pasted === null
        ? null
        : this.sanitizeDecimal(
            this.displayValue.slice(0, start) + pasted + this.displayValue.slice(end),
          );

    if (merged === null) {
      this.rejectNotice = 'הערך שהודבק אינו סכום תקין — הסכום לא שונה';
      return;
    }
    this.rejectNotice = null;
    target.value = merged;
    this.displayValue = merged;
    this.emitDecimal(merged);
  }

  private insertAtCaret(
    target: HTMLInputElement,
    text: string,
    start: number,
    end: number,
  ): void {
    const charsAfterCaret = this.displayValue.length - end;
    const next = this.sanitizeDecimal(
      this.displayValue.slice(0, start) + text + this.displayValue.slice(end),
    );
    if (next === null) {
      this.rejectNotice = 'ניתן להזין ספרות ונקודה אחת בלבד (עד 2 ספרות אחרי הנקודה)';
      return;
    }
    this.rejectNotice = null;
    target.value = next;
    this.displayValue = next;
    this.emitDecimal(next);
    const caret = next.length - charsAfterCaret;
    target.setSelectionRange(caret, caret);
  }

  /**
   * Validate a (possibly partial) decimal amount. Returns the normalised
   * string, or null when it is not a valid amount — the caller then keeps
   * the old value. Never strips characters to "make it fit".
   */
  private sanitizeDecimal(raw: string): string | null {
    if (raw === '') return '';
    if (!/^\d*\.?\d*$/.test(raw)) return null;
    const [whole, fraction] = raw.split('.');
    if (fraction !== undefined && fraction.length > this.decimals) return null;
    const wholeClean = whole.replace(/^0+(?=\d)/, '');
    if (fraction === undefined) return wholeClean;
    return `${wholeClean === '' ? '0' : wholeClean}.${fraction}`;
  }

  private emitDecimal(value: string): void {
    const n = value === '' ? NaN : Number(value);
    this.currentValue = Number.isFinite(n) ? n : null;
    this.onChange(this.currentValue);
  }

  // ── Internals ───────────────────────────────────────────────────────────

  /**
   * Strip every character that isn't a digit (or leading minus, if allowed).
   * Also normalises away leading zeros (so "020" → "20") while keeping
   * a single "0" so the field can hold zero.
   */
  private sanitize(raw: string): string {
    let s = raw;
    let negative = false;

    if (this.allowNegative && s.startsWith('-')) {
      negative = true;
      s = s.slice(1);
    }

    s = s.replace(/[^0-9]/g, '');
    s = s.replace(/^0+(?=\d)/, '');

    if (s === '') {
      return negative ? '-' : '';
    }
    return negative ? `-${s}` : s;
  }

  private emit(cleaned: string): void {
    if (cleaned === '' || cleaned === '-') {
      this.onChange(null);
      return;
    }
    const n = Number(cleaned);
    if (!Number.isFinite(n) || !Number.isSafeInteger(n)) {
      this.onChange(null);
      return;
    }
    this.onChange(n);
  }
}
